import { useEffect, useState } from "react";
import ModalV2 from "../../ModalV2";
import CodeEditor from "../../CodeEditor";
import useModal from "../../../hooks/useModal";
import useSourceCode from "../../../hooks/useSourceCode";
import toast from "react-hot-toast";
import { Button } from "@oc-labs/wt-storybook";
import { X } from "lucide-react";
import PropTypes from "prop-types";

const empty = { name: "", content: "" };

export default function UpsertSourceCodeModal({ onUpsert }) {
  const { adminSourceCodeByName, adminCreateSourceCode, adminUpdateSourceCode } = useSourceCode();
  const { close, data, isOpen } = useModal("upsert-source-code-modal");
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [loadingContent, setLoadingContent] = useState(false);

  const isEdit = Boolean(data?.name && !data?.__isNew);

  useEffect(() => {
    if (!isOpen) return;
    if (isEdit) {
      setForm({ name: data.name, content: data.content ?? "" });
      if (data.content == null) {
        setLoadingContent(true);
        adminSourceCodeByName(data.name)
          .then((res) => {
            const content = res?.content ?? res?.body ?? "";
            setForm({ name: data.name, content });
          })
          .catch((err) =>
            toast.error(err?.response?.data?.message || err.message || "Failed to load source"))
          .finally(() => setLoadingContent(false));
      }
    } else {
      setForm(empty);
    }
  }, [isOpen, data?.name]);

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error("Name is required");
      return;
    }
    setSaving(true);
    try {
      if (isEdit) {
        await adminUpdateSourceCode(form.name, { content: form.content });
        toast.success("Source code updated");
      } else {
        await adminCreateSourceCode({ name: form.name.trim(), content: form.content });
        toast.success("Source code created");
      }
      onUpsert?.();
      close();
    } catch (error) {
      toast.error(error?.response?.data?.message || error.message || "Failed to save source code");
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalV2 name="upsert-source-code-modal" className="w-[min(1100px,95vw)] h-[85vh]">
      <div className="flex flex-col h-full w-full">
        <div className="shrink-0 p-8 border-b border-gray-300 bg-gradient-to-br from-blue-600 to-indigo-700 rounded-xl">
          <div className="flex justify-between items-start">
            <div className="flex flex-col gap-2">
              <h1 className="text-white text-3xl font-black leading-tight tracking-[-0.033em]">
                {isEdit ? "Edit Source Code" : "Add New Source Code"}
              </h1>
              <p className="text-blue-100 text-base font-normal">
                Manage smart contract source files. Edit directly in the browser.
              </p>
            </div>
            <button className="text-gray-200 hover:text-white transition-colors" onClick={() => close()}>
              <X />
            </button>
          </div>
        </div>

        <div className="shrink-0 px-8 pt-6">
          <label className="flex flex-col gap-2">
            <p className="text-gray-900 text-sm font-medium">
              Name {isEdit && <span className="text-xs text-gray-500 font-normal">(read-only)</span>}
            </p>
            <input
              className={`form-input w-full rounded-lg text-gray-900 focus:outline-0 focus:ring-2 focus:ring-primary border border-gray-300 h-12 placeholder:text-gray-400 px-4 text-base font-normal ${
                isEdit ? "bg-gray-100 cursor-not-allowed" : "bg-white"
              }`}
              placeholder="e.g. ERC20.sol"
              value={form.name}
              disabled={isEdit}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
          </label>
        </div>

        <div className="flex-1 min-h-0 px-8 pt-4 pb-4 flex flex-col gap-2">
          <div className="flex items-center justify-between shrink-0">
            <p className="text-gray-900 text-sm font-medium">Source Code</p>
            <p className="text-xs text-gray-500">
              {loadingContent
                ? "Loading…"
                : `${form.content.split("\n").length} lines · ${form.content.length} chars`}
            </p>
          </div>
          <CodeEditor
            value={form.content}
            language="solidity"
            onChange={(content) => setForm({ ...form, content })}
            placeholder="// SPDX-License-Identifier: MIT&#10;pragma solidity ^0.8.20;&#10;&#10;contract MyContract { ... }"
            fillHeight
            className="flex-1 min-h-0"
            readOnly={loadingContent}
          />
        </div>

        <div className="shrink-0 bg-white border-t border-gray-200 px-8 py-4 flex justify-end gap-4">
          <Button type="tertiary" onClick={() => close()}>
            Cancel
          </Button>
          <Button type="primary" onClick={handleSave} disabled={saving || loadingContent}>
            {saving ? "Saving…" : isEdit ? "Update" : "Create"}
          </Button>
        </div>
      </div>
    </ModalV2>
  );
}

UpsertSourceCodeModal.propTypes = {
  onUpsert: PropTypes.func,
};
